import React, { useState, useEffect } from 'react';
import { useTranslation } from './i18n/LanguageContext';

const HolidaysPage = ({ isDarkMode, currentUser, userPlan, onNavigate }) => {
  const { t } = useTranslation();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
  // App-shell caches: read synchronously so holidays + upcoming + page chrome
  // render from frame 1 instead of the loading spinner on Ctrl+R.
  const _readHpCache = (key) => {
    try { const c = localStorage.getItem(key); if (c) return JSON.parse(c); } catch {}
    return null;
  };
  const _cachedHolidays = _readHpCache('hpHolidaysCache');
  const _cachedUpcoming = _readHpCache('hpUpcomingCache');
  const _cachedInfoSteps = _readHpCache('hpInfoStepsCache');
  const _cachedPs = _readHpCache('hpPageSettingsCache');

  const [holidays, setHolidays] = useState(_cachedHolidays || []);
  const [upcoming, setUpcoming] = useState(_cachedUpcoming || []);
  // Skip the loading spinner if we already have cached holidays
  const [loading, setLoading] = useState(!_cachedHolidays);
  const [activeType, setActiveType] = useState('all'); // all, islamic, national, cultural
  const [highlightedHoliday, setHighlightedHoliday] = useState(null);
  const [infoSteps, setInfoSteps] = useState(_cachedInfoSteps || []);
  const [ps, setPs] = useState(_cachedPs || {});
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    loadHolidays();
    loadUpcoming();
    loadInfoSteps();
    loadPageSettings();
    
    // Check URL for highlighted holiday
    const urlParams = new URLSearchParams(window.location.search);
    const highlightId = urlParams.get('highlight');
    if (highlightId) {
      setHighlightedHoliday(highlightId);
      // Scroll to it after holidays load
      setTimeout(() => {
        const element = document.getElementById(`holiday-${highlightId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 500);
    }
  }, []);

  // Show "back to top" button when scrolled down
  useEffect(() => {
    const handleScroll = () => setShowBackToTop(window.scrollY > 300);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const loadHolidays = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/holidays`);
      if (response.ok) {
        const data = await response.json();
        const list = data.holidays || [];
        setHolidays(list);
        try { localStorage.setItem('hpHolidaysCache', JSON.stringify(list)); } catch {}
      } else {
        console.error('Failed to load holidays:', response.status);
        setHolidays([]);
      }
    } catch (error) {
      console.error('Error loading holidays:', error);
      setHolidays([]);
    } finally {
      setLoading(false);
    }
  };

  const loadUpcoming = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/holidays/upcoming`);
      if (response.ok) {
        const data = await response.json();
        const list = data.upcoming || [];
        setUpcoming(list);
        try { localStorage.setItem('hpUpcomingCache', JSON.stringify(list)); } catch {}
      } else {
        console.error('Failed to load upcoming:', response.status);
        setUpcoming([]);
      }
    } catch (error) {
      console.error('Error loading upcoming holidays:', error);
      setUpcoming([]);
    }
  };

  const loadPageSettings = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/holidays/page-settings`);
      if (res.ok) {
        const d = await res.json();
        setPs(d);
        try { localStorage.setItem('hpPageSettingsCache', JSON.stringify(d)); } catch {}
      }
    } catch {}
  };

  const ri = (field, fallback, cls = "w-6 h-6 object-contain inline") => {
    const url = ps[field];
    return url ? <img src={`${BACKEND_URL}${url}`} alt="" className={cls} /> : fallback;
  };

  const loadInfoSteps = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/holidays/info-steps`);
      if (res.ok) {
        const data = await res.json();
        const steps = data.steps || [];
        setInfoSteps(steps);
        try { localStorage.setItem('hpInfoStepsCache', JSON.stringify(steps)); } catch {}
      }
    } catch (err) {
      console.error('Error loading info steps:', err);
    }
  };

  const filteredHolidays = holidays
    .filter(h => {
      if (activeType === 'all') return true;
      return h.type === activeType;
    })
    .sort((a, b) => {
      // Sort by date_2026 chronologically
      const dateA = new Date(a.date_2026 + 'T00:00:00');
      const dateB = new Date(b.date_2026 + 'T00:00:00');
      return dateA - dateB;
    });

  const getTypeColor = (type) => {
    switch (type) {
      case 'islamic': return 'from-purple-500 to-indigo-600';
      case 'national': return 'from-red-500 to-pink-600';
      case 'cultural': return 'from-green-500 to-emerald-600';
      default: return 'from-gray-500 to-slate-600';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'islamic': return ri('type_islamic_icon_url', '\u262a\ufe0f');
      case 'national': return ri('type_national_icon_url', '\ud83c\uddf2\ud83c\udde6');
      case 'cultural': return ri('type_cultural_icon_url', '\ud83c\udfa8');
      default: return ri('type_default_icon_url', '\ud83d\udcc5');
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
        <div className="text-center">
          <div className="animate-spin inline-block w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full mb-4"></div>
          <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>{t('loading_holidays')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4">
      <style>{`
        @keyframes glow {
          0%, 100% { box-shadow: 0 0 20px rgba(234, 179, 8, 0.5); }
          50% { box-shadow: 0 0 40px rgba(234, 179, 8, 0.8), 0 0 60px rgba(234, 179, 8, 0.6); }
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          25% { transform: translateY(-10px) rotate(5deg); }
          50% { transform: translateY(-15px) rotate(-5deg); }
          75% { transform: translateY(-8px) rotate(3deg); }
        }
        
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        @keyframes bounce-gentle {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }
        
        @keyframes flash-text {
          0%, 100% { opacity: 1; text-shadow: 0 0 10px rgba(255, 255, 255, 0.5); }
          50% { opacity: 0.8; text-shadow: 0 0 20px rgba(255, 215, 0, 0.8), 0 0 30px rgba(255, 69, 0, 0.6); }
        }
        
        @keyframes gradient-shift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        
        @keyframes sparkle {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.2); }
        }
        
        @keyframes confetti-fall {
          0% { transform: translateY(-20px) rotate(0deg); opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
        }
        
        .float-icon {
          animation: float 3s ease-in-out infinite;
        }
        
        .spin-slow {
          animation: spin-slow 8s linear infinite;
        }
        
        .bounce-gentle {
          animation: bounce-gentle 2s ease-in-out infinite;
        }
        
        .flash-text {
          animation: flash-text 2s ease-in-out infinite;
        }
        
        .gradient-shift {
          background-size: 200% 200%;
          animation: gradient-shift 3s ease infinite;
        }
        
        .sparkle {
          animation: sparkle 1.5s ease-in-out infinite;
        }
      `}</style>
      {/* Back to top - mobile, appears when scrolling down */}
      {showBackToTop && (
        <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className={`lg:hidden fixed top-[68px] left-4 z-40 flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium shadow-md transition-opacity ${isDarkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-white text-gray-600 hover:bg-gray-100'}`}>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
          </svg>
          Top
        </button>
      )}

      {/* Profile Header Card - Same as Profile/Dashboard */}
      {currentUser && (
        <div className={`p-6 rounded-lg shadow-lg mb-6 ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white'}`}>
          <div className="flex items-center gap-4">
            <div className={`text-6xl ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} rounded-full w-24 h-24 flex items-center justify-center overflow-hidden`}>
              {(() => {
                const raw = currentUser.avatar?.image_url;
                const imgUrl = raw?.startsWith('/') ? `${BACKEND_URL}${raw}` : raw?.replace('?dl=0', '?raw=1').replace('www.dropbox.com', 'dl.dropboxusercontent.com');
                return imgUrl ? (
                  <img 
                    src={imgUrl} 
                    alt="Avatar"
                    className="w-full h-full object-cover object-top"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null;
              })()}
              <span className={`${currentUser.avatar?.image_url ? 'hidden' : 'flex'} items-center justify-center w-full h-full`}>
                {currentUser.full_name?.charAt(0) || '👤'}
              </span>
            </div>
            <div className="flex-1">
              <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                {currentUser.full_name}
              </h2>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {currentUser.email}
              </p>
              <div className="flex gap-2 mt-2">
                {(() => {
                  // Prefer App-level userPlan (same source as sidebar) over currentUser.plan
                  // which is never populated. Hides badge cleanly if neither resolved yet.
                  const planForBadge = userPlan?.display_name ? userPlan : currentUser.plan;
                  if (!planForBadge?.display_name) return null;
                  const isFree = planForBadge.name === 'free';
                  return (
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      isFree
                        ? isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
                        : isDarkMode ? 'bg-blue-700 text-blue-200' : 'bg-blue-100 text-blue-700'
                    }`}>
                      {planForBadge.display_name}
                    </span>
                  );
                })()}
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  currentUser.status === 'active'
                    ? 'bg-green-500 text-white'
                    : 'bg-red-500 text-white'
                }`}>
                  {currentUser.status || 'active'}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className={`${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        {/* Header */}
        <div className={`text-center mb-4 md:mb-8 p-4 md:p-6 rounded-xl md:rounded-2xl ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white shadow-md'}`}>
          <h2 className={`text-2xl md:text-4xl font-black mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            {ri('header_icon_url', '\ud83c\uddf2\ud83c\udde6', 'w-8 h-8 object-contain inline')} {t('moroccan_holidays')}
          </h2>
          <p className={`text-sm md:text-lg ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            {t('celebrate_morocco_culture')}
          </p>
        </div>

        {/* Upcoming Holidays Banner */}
        {upcoming.length > 0 && (
          <div className="mb-4 md:mb-8 bg-gradient-to-r from-yellow-400 via-orange-400 to-red-400 rounded-xl md:rounded-2xl p-4 md:p-6 shadow-2xl gradient-shift relative overflow-hidden">
            {/* Floating celebration icons - hidden on mobile */}
            <div className="hidden md:block absolute top-2 right-4 text-3xl float-icon">{ri('floating_party_icon_url', '\ud83c\udf8a', 'w-8 h-8 object-contain')}</div>
            <div className="hidden md:block absolute top-8 right-20 text-2xl spin-slow">{ri('floating_sparkle_icon_url', '\u2728', 'w-7 h-7 object-contain')}</div>
            <div className="hidden md:block absolute bottom-4 left-6 text-3xl bounce-gentle">{ri('floating_balloon_icon_url', '\ud83c\udf88', 'w-8 h-8 object-contain')}</div>
            <div className="hidden md:block absolute bottom-8 right-12 text-2xl sparkle">{ri('floating_star_icon_url', '\u2b50', 'w-7 h-7 object-contain')}</div>
            
            <div className="flex items-center gap-3 md:gap-4 mb-3 md:mb-4 relative z-10">
              <div className="text-3xl md:text-5xl bounce-gentle">{ri('upcoming_main_icon_url', '\ud83c\udf89', 'w-10 h-10 md:w-14 md:h-14 object-contain')}</div>
              <div>
                <h2 className="text-xl md:text-3xl font-black text-white">
                  {t('upcoming')} {ri('upcoming_star_icon_url', '\ud83c\udf1f')}
                </h2>
                <p className="text-white text-opacity-95 font-semibold text-sm md:text-lg">{t('dont_forget_wish')} {ri('upcoming_heart_icon_url', '\u2764\ufe0f')}</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
              {upcoming.map(holiday => (
                <button 
                  key={holiday.id} 
                  onClick={() => {
                    setHighlightedHoliday(holiday.id);
                    setTimeout(() => {
                      const element = document.getElementById(`holiday-${holiday.id}`);
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }
                    }, 100);
                  }}
                  className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg md:rounded-xl p-3 md:p-4 border-2 border-white border-opacity-30 hover:bg-opacity-30 transition-all cursor-pointer text-left"
                >
                  <div className="flex items-start gap-2 md:gap-3">
                    <div className="text-2xl md:text-3xl">
                      {holiday.icon_url
                        ? <img src={`${BACKEND_URL}${holiday.icon_url}`} alt="" className="w-8 h-8 md:w-10 md:h-10 object-contain" />
                        : holiday.icon}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-white text-base md:text-lg">{holiday.name_en}</h3>
                      <p className="text-white text-opacity-80 text-xs">{holiday.date}</p>
                      <div className="mt-1 md:mt-2 flex items-center gap-2">
                        <span className="bg-white bg-opacity-30 px-2 md:px-3 py-1 rounded-full text-white text-xs md:text-sm font-bold animate-pulse">
                          {holiday.days_until === 0 ? `🎊 ${t('today_exclaim')}` : `⏰ ${t('in_x_days').replace('{days}', holiday.days_until)}`}
                        </span>
                        <span className="text-white text-opacity-70 text-xs">↓ {t('click_to_view')}</span>
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Filter Tabs */}
        <div className="mb-4 md:mb-6 flex flex-wrap gap-2 md:gap-3 justify-center">
          {[
            { id: 'all', label: t('all'), icon: ri('filter_all_icon_url', '\ud83c\udf8a') },
            { id: 'islamic', label: t('islamic'), icon: ri('type_islamic_icon_url', '\u262a\ufe0f') },
            { id: 'national', label: t('national'), icon: ri('type_national_icon_url', '\ud83c\uddf2\ud83c\udde6') },
            { id: 'cultural', label: t('cultural'), icon: ri('type_cultural_icon_url', '\ud83c\udfa8') }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveType(tab.id)}
              className={`px-3 md:px-6 py-2 md:py-3 rounded-lg md:rounded-xl font-bold text-sm md:text-base transition-all ${
                activeType === tab.id
                  ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg scale-105'
                  : isDarkMode ? 'bg-gray-800 text-gray-300 hover:shadow-md' : 'bg-white text-gray-700 hover:shadow-md'
              }`}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {/* Holidays Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {filteredHolidays.map(holiday => {
            const isHighlighted = highlightedHoliday === holiday.id;
            const isUpcoming = upcoming.some(u => u.id === holiday.id);
            
            return (
            <div
              key={holiday.id}
              id={`holiday-${holiday.id}`}
              className={`rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all hover:-translate-y-1 relative ${
                isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white'
              } ${
                isHighlighted || isUpcoming
                  ? 'ring-4 ring-yellow-400 animate-pulse shadow-2xl shadow-yellow-500/50'
                  : ''
              }`}
              style={isHighlighted || isUpcoming ? {
                animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite, glow 2s ease-in-out infinite'
              } : {}}
            >
              {/* Floating celebration icons - ONLY for upcoming holidays */}
              {isUpcoming && (
                <>
                  <div className="absolute top-2 right-2 text-2xl float-icon z-20">{ri('highlight_party_icon_url', '\ud83c\udf89', 'w-7 h-7 object-contain')}</div>
                  <div className="absolute top-4 right-12 text-xl sparkle z-20">{ri('highlight_sparkle_icon_url', '\u2728', 'w-6 h-6 object-contain')}</div>
                  <div className="absolute top-10 right-6 text-xl bounce-gentle z-20">{ri('highlight_confetti_icon_url', '\ud83c\udf8a', 'w-6 h-6 object-contain')}</div>
                </>
              )}
              
              {/* Card Header */}
              <div className={`bg-gradient-to-r ${getTypeColor(holiday.type)} p-3 md:p-6 text-white relative`}>
                <div className="flex items-center justify-between mb-1 md:mb-3">
                  <div className={`text-3xl md:text-5xl ${isUpcoming ? 'bounce-gentle' : ''}`}>
                    {holiday.icon_url
                      ? <img src={`${BACKEND_URL}${holiday.icon_url}`} alt="" className="w-10 h-10 md:w-14 md:h-14 object-contain" />
                      : holiday.icon}
                  </div>
                  <div className={`text-xl md:text-2xl ${isUpcoming ? 'float-icon' : ''}`}>{getTypeIcon(holiday.type)}</div>
                </div>
                <h3 className={`text-lg md:text-2xl font-black mb-1 ${isUpcoming ? 'flash-text' : ''}`}>{holiday.name_en}</h3>
                <p className="text-white text-opacity-90 font-semibold text-sm md:text-base">{holiday.name_darija}</p>
                <p className="text-white text-opacity-75 text-xs md:text-sm mt-1">{holiday.name_ar}</p>
              </div>

              {/* Card Content */}
              <div className="p-3 md:p-6 relative">
                {/* Date Display */}
                {holiday.date_2026 && (
                  <div className={`mb-3 md:mb-4 rounded-lg md:rounded-xl p-2 md:p-4 text-center shadow-md gradient-shift relative overflow-hidden ${
                    isDarkMode ? 'bg-gradient-to-r from-purple-900/40 via-pink-900/40 to-red-900/40' : 'bg-gradient-to-r from-purple-100 via-pink-100 to-red-100'
                  }`}>
                    <div className="absolute top-0 right-2 text-lg md:text-xl sparkle hidden md:block">{ri('date_sparkle_icon_url', '\ud83d\udcab')}</div>
                    <p className={`text-xs font-bold mb-1 uppercase tracking-wide ${isDarkMode ? 'text-purple-300' : 'text-purple-700'}`}>📅 2026:</p>
                    <p className={`text-base md:text-xl font-black ${isDarkMode ? 'text-purple-200' : 'text-purple-900'} ${isUpcoming ? 'flash-text' : ''}`}>
                      {new Date(holiday.date_2026 + 'T00:00:00').toLocaleDateString('en-US', { 
                        month: 'short', 
                        day: 'numeric', 
                        year: 'numeric' 
                      })}
                    </p>
                    {holiday.type === 'islamic' && (
                      <p className={`text-xs mt-1 font-semibold ${isDarkMode ? 'text-purple-300' : 'text-purple-600'}`}>
                        ☪️ {t('date_may_vary')}
                      </p>
                    )}
                  </div>
                )}

                <div className="mb-2 md:mb-4">
                  <p className={`text-xs md:text-sm leading-relaxed line-clamp-2 md:line-clamp-none ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    {holiday.description_en}
                  </p>
                </div>

                {/* Greeting Box */}
                <div className={`rounded-lg md:rounded-xl p-3 md:p-5 mb-2 md:mb-4 shadow-lg gradient-shift relative overflow-hidden border md:border-2 ${
                  isDarkMode
                    ? 'bg-gradient-to-r from-yellow-900/30 via-orange-900/30 to-red-900/30 border-yellow-700'
                    : 'bg-gradient-to-r from-yellow-100 via-orange-100 to-red-100 border-yellow-300'
                }`}>
                  <p className={`text-xs font-black mb-1 uppercase tracking-wider ${isDarkMode ? 'text-orange-300' : 'text-orange-700'}`}>
                    {ri('greeting_icon_url', '\ud83d\udc4b')} {t('greeting_label')}
                  </p>
                  <p className={`font-black text-base md:text-xl mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{holiday.greeting_darija}</p>
                  <p className={`text-xs md:text-sm font-semibold italic ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{holiday.greeting_en}</p>
                </div>

                {/* Cultural Notes - Hidden on mobile */}
                {holiday.cultural_notes && (
                  <div className={`hidden md:block rounded-xl p-5 mb-4 shadow-md border-2 relative overflow-hidden ${
                    isDarkMode
                      ? 'bg-gradient-to-r from-blue-900/30 via-cyan-900/30 to-teal-900/30 border-blue-700'
                      : 'bg-gradient-to-r from-blue-100 via-cyan-100 to-teal-100 border-blue-200'
                  }`}>
                    <div className="absolute top-2 right-2 text-lg sparkle">💎</div>
                    <p className={`text-xs font-black mb-3 uppercase tracking-wider flex items-center gap-2 ${isDarkMode ? 'text-blue-300' : 'text-blue-800'}`}>
                      <span className="bounce-gentle">{ri('cultural_insight_icon_url', '\ud83d\udca1')}</span> {t('cultural_insight')}
                    </p>
                    <p className={`text-sm font-semibold leading-relaxed ${isDarkMode ? 'text-blue-200' : 'text-blue-800'}`}>
                      {holiday.cultural_notes}
                    </p>
                  </div>
                )}

                {/* Duration */}
                {holiday.duration_days && holiday.duration_days > 1 && (
                  <div className={`flex items-center gap-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    <span>{ri('duration_icon_url', '\ud83d\udcc5')}</span>
                    <span>{holiday.duration_days} {t('days_celebration')}</span>
                  </div>
                )}
              </div>
            </div>
            );
          })}
        </div>

        {filteredHolidays.length === 0 && (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">{ri('no_results_icon_url', '\ud83d\udd0d', 'w-16 h-16 object-contain mx-auto')}</div>
            <p className={`text-lg ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('no_holidays_found')}</p>
          </div>
        )}

        {/* How Notifications Work - Explanation Section */}
        {infoSteps.length > 0 && (
        <div className={`mt-12 rounded-2xl p-8 border-2 shadow-lg ${
          isDarkMode
            ? 'bg-gray-700 border-gray-600'
            : 'bg-gradient-to-r from-blue-50 via-purple-50 to-pink-50 border-blue-200'
        }`}>
          <div className="flex items-center gap-3 mb-6">
            <span className="text-4xl">{ri('info_section_icon_url', '\ud83d\udce7', 'w-10 h-10 object-contain')}</span>
            <h3 className={`text-2xl font-black ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {t('how_notifications_work')}
            </h3>
          </div>

          <div className="space-y-6">
            {infoSteps.map((step, idx) => (
              <div key={step.id} className="flex gap-4">
                <div className={`flex-shrink-0 w-12 h-12 ${step.color || 'bg-blue-500'} text-white rounded-full flex items-center justify-center font-bold text-xl`}>
                  {idx + 1}
                </div>
                <div>
                  <h4 className={`font-bold text-lg ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-2`}>
                    {step.icon_url
                      ? <img src={`${BACKEND_URL}${step.icon_url}`} alt="" className="w-6 h-6 object-contain inline" />
                      : step.icon} {step.title}
                  </h4>
                  <p className={`leading-relaxed ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                    {step.description}
                    {step.highlight_text && (
                      <> <span className={`font-bold ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>{step.highlight_text}</span></>
                    )}
                  </p>
                </div>
              </div>
            ))}
          </div>

        </div>
        )}
      </div>
    </div>
  );
};

export default HolidaysPage;
